import React, { useEffect, useRef, useState } from "react";
import { BoxArrowRight, ChevronLeft, Shop } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  createStore,
  getStoreDetails,
  updateStore,
} from "../../services/storeServices";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useTranslation } from "react-i18next";
import {
  getCityList,
  getCountryList,
  getStateList,
} from "../../services/commonServices";
import Select from "react-select";
import { customStyles } from "../../utils/function";
import { useDispatch, useSelector } from "react-redux";
import CONFIG from "../../config";
import { authActions, store } from "../../store";
import { notify } from "../../components/Common/Alerts";
import { useConfig } from "../../components/Context/ConfigContext";
import { logout } from "../../services/authServices";

const StoreFrontAdd = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { configData } = useConfig();

  const [formData, setFormData] = useState({
    store_name: "",
    store_mobile: "",
    store_business_type: "",
    store_address: "",
    store_landmark: "",
    store_city_id: "",
    store_state_id: "",
    store_country_id: "",
    store_pincode: "",
    store_latitude: "",
    store_longitude: "",
    store_gst_number: "",
    store_pan_number: "",
    uuid: "",
  });

  const [errors, setErrors] = useState({
    full_name: "",
    mobile: "",
    email: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [storeDetails, setStoreDetails] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInitialized = useRef(false);

  const validateField = (name, value) => {
    switch (name) {
      case "store_name":
        if (!value) return "Name is required";
        return "";

      case "store_mobile":
        if (!value) return "Mobile number is required";
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return "Enter a valid 10-digit mobile number";
        }
        return "";

      case "store_address":
        if (!value) return "Address is required";
        return "";

      case "store_landmark":
        if (!value) return "Landmark is required";
        return "";

      case "store_city_id":
        if (!value) return "City is required";
        return "";

      case "store_country_id":
        if (!value) return "Country is required";
        return "";

      case "store_state_id":
        if (!value) return "State is required";
        return "";

      case "store_pincode":
        if (!value) return "Pincode is required";
        return "";

      case "store_business_type":
        if (!value) return "Business type is required";
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "store_mobile") {
      const cleanedValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        store_mobile: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(() => {
    if (!window.google || mapInitialized.current) return;

    mapInitialized.current = true;

    const hasSavedLocation =
      formData?.store_latitude && formData?.store_longitude;

    const initMap = (lat, lng) => {
      if (!mapRef.current) return; //  guard

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 15,
      });

      const markerInstance = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstance,
        draggable: true,
      });

      setMap(mapInstance);
      setMarker(markerInstance);

      markerInstance.addListener("dragend", () => {
        const pos = markerInstance.getPosition();

        const newLat = pos.lat();
        const newLng = pos.lng();

        setFormData((prev) => ({
          ...prev,
          store_latitude: newLat,
          store_longitude: newLng,
        }));

        getAddressFromLatLng(newLat, newLng);
      });
    };

    if (hasSavedLocation) {
      initMap(
        Number(formData.store_latitude),
        Number(formData.store_longitude),
      );
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setFormData((prev) => ({
            ...prev,
            store_latitude: lat,
            store_longitude: lng,
          }));

          initMap(lat, lng);
        },
        (error) => {
          console.log("GPS error:", error);

          initMap(23.0225, 72.5714);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      initMap(23.0225, 72.5714);
    }
  }, [formData?.store_latitude, formData?.store_longitude]);

  const getAddressFromLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const result = results[0];

        let address = result.formatted_address;
        let landmark = "";
        let pincode = "";

        //extract components
        result.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("postal_code")) {
            pincode = component.long_name;
          }

          if (
            types.includes("sublocality_level_1") ||
            types.includes("sublocality") ||
            types.includes("neighborhood")
          ) {
            landmark = component.long_name;
          }
        });

        setFormData((prev) => ({
          ...prev,
          store_address: address,
          store_landmark: landmark || prev.landmark,
          store_pincode: pincode || prev.pincode,
        }));
      }
    });
  };

  const getLatLngFromAddress = (address) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const lat = location.lat();
        const lng = location.lng();

        setFormData((prev) => ({
          ...prev,
          store_latitude: lat,
          store_longitude: lng,
        }));

        if (map) {
          map.setCenter({ lat, lng });
        }

        if (marker) {
          marker.setPosition({ lat, lng });
        }
      }
    });
  };

  useEffect(function () {
    getCountryList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCountryList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (formData?.store_country_id) {
      let data = {
        country_id: formData.store_country_id,
      };

      getStateList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setStateList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.store_country_id]);

  useEffect(() => {
    if (formData?.store_state_id) {
      let data = {
        state_id: formData.store_state_id,
      };

      getCityList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setCityList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.store_state_id]);

  useEffect(
    function () {
      if (uuid) {
        let data = {
          uuid: uuid,
        };
        getStoreDetails(data)
          .then((response) => {
            const decrypted = decryptData(response.data.data);
            setStoreDetails(decrypted.data);
          })
          .catch((err) => {
            navigate("/owner-store");
            notify.error(Utils.getErrorMessage(err));
          });
      }
    },
    [navigate, uuid],
  );

  useEffect(
    function () {
      if (storeDetails) {
        setFormData({
          store_name: storeDetails?.store_name,
          store_mobile: storeDetails?.store_mobile,
          store_business_type: storeDetails?.store_business_type,
          store_address: storeDetails?.store_address,
          store_landmark: storeDetails?.store_landmark,
          store_city_id: storeDetails?.store_city_id,
          store_state_id: storeDetails?.store_state_id,
          store_country_id: storeDetails?.store_country_id,
          store_pincode: storeDetails?.store_pincode,
          store_longitude: storeDetails?.store_longitude,
          uuid: storeDetails?.uuid,
        });
      }
    },
    [storeDetails],
  );

  const handleMobileValidation = () => {
    let errorMsg = "";

    const mobileRegex = /^\d{10}$/;

    if (!formData.store_mobile) {
      errorMsg = "Mobile is required";
    } else if (!mobileRegex.test(formData.store_mobile)) {
      errorMsg = "Enter a valid 10-digit mobile number";
    }

    setErrors((prev) => ({
      ...prev,
      mobile: errorMsg,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      store_name: validateField("store_name", formData.store_name),
      store_mobile: validateField("store_mobile", formData.store_mobile),
      store_address: validateField("store_address", formData.store_address),
      store_city_id: validateField("store_city_id", formData.store_city_id),
      store_country_id: validateField(
        "store_country_id",
        formData.store_country_id,
      ),
      store_state_id: validateField("store_state_id", formData.store_state_id),
      store_pincode: validateField("store_pincode", formData.store_pincode),
      store_business_type: validateField(
        "store_business_type",
        formData.store_business_type,
      ),
      store_landmark: validateField("store_landmark", formData?.store_landmark),
    };

    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;
    setBtnLoading(true);

    const payload = {
      name: formData?.store_name?.trim(),
      mobile: formData?.store_mobile,
      gst_number: formData?.store_gst_number,
      pan_number: formData?.store_pan_number,
      business_type: formData?.store_business_type,
      address: formData?.store_address?.trim(),
      landmark: formData?.store_landmark,
      city_id: formData?.store_city_id,
      state_id: formData?.store_state_id,
      country_id: formData?.store_country_id,
      pincode: formData?.store_pincode,
      latitude: formData?.store_latitude,
      longitude: formData?.store_longitude,
      ...(formData?.uuid && { uuid: formData.uuid }),
    };

    const apiCall = formData?.uuid ? updateStore : createStore;

    try {
      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);
      const storedData = JSON.parse(localStorage.getItem(CONFIG.login_data));

      const updatedData = {
        ...storedData,
        is_store_selected: decrypted?.is_store_selected,
        active_store_id: decrypted?.active_store_id,
        store_name: decrypted?.store_name,
        user: {
          ...storedData.user,
          active_store_id: decrypted?.active_store_id, // from your new API response
        },
      };
      localStorage.setItem(CONFIG.login_data, JSON.stringify(updatedData));
      // notify.success(decrypted.data.message);
      setFormData({
        store_name: "",
        store_mobile: "",
        store_business_type: "",
        store_address: "",
        store_landmark: "",
        store_city_id: "",
        store_state_id: "",
        store_country_id: "",
        store_pincode: "",
        store_latitude: "",
        store_longitude: "",
        uuid: "",
      });
      setErrors("");
      navigate("/store");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const businessOptions = configData?.business_type || [];

  const selectedOption =
    businessOptions.find((opt) => opt.value === formData.store_business_type) ||
    null;

  const onLogout = (e) => {
    logout()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        store.dispatch(authActions.logout());
        navigate(`/`);
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
        <div className="max-auto space-y-8 animate-fade-up max-w-7xl mx-auto">
          {/* HEADER SECTION */}
          <div className="flex items-center gap-4 md:gap-6 px-2">
            <button
              onClick={() => setShowLogoutModal(true)}
              className="group flex items-center justify-center px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-all active:scale-95 shadow-sm"
            >
              <BoxArrowRight
                size={18}
                className="group-hover:-translate-x-1 transition-transform"
              />
              <span className="hidden md:block ml-2 font-bold text-sm">
                {t("logOut")}
              </span>
            </button>

            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight">
                {uuid ? t("updateNewStore") : t("addNewStore")}
              </h2>
              <p className="text-gray-400 font-medium text-sm">
                {t("setupPartnerProfile")}
              </p>
            </div>
          </div>

          {/* MAIN CARD */}
          <section className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
            {/* SECTION TITLE & TOGGLE */}
            <div className="flex items-center justify-between p-8 border-b border-slate-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-50 text-brand-orange rounded-2xl flex items-center justify-center shadow-inner">
                  <Shop size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-800">
                    {t("storeDetails")}{" "}
                  </h2>
                  <p className="text-xs text-slate-400">
                    Enter your business location and contact info
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="store_name" className="input-label">
                    {t("storeName")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="store_name"
                    placeholder="Enter store name"
                    className={`input-field ${errors.store_name ? "input-error" : ""}`}
                    value={formData?.store_name || ""}
                    onChange={handleChange}
                  />
                  {errors.store_name && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.store_name}
                    </span>
                  )}
                </div>

                {/* Mobile Number */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="store_mobile" className="input-label">
                    {t("mobileNumber")}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="store_mobile"
                    placeholder="Enter mobile number"
                    className={`input-field ${errors.store_mobile ? "input-error" : ""}`}
                    value={formData?.store_mobile || ""}
                    onChange={handleChange}
                    onKeyDown={(e) =>
                      ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
                    }
                    min={0}
                    onBlur={handleMobileValidation}
                  />
                  {errors.store_mobile && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.store_mobile}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label htmlFor="store_address" className="input-label">
                    {t("address")}
                    <span className="text-red-500">*</span>{" "}
                  </label>
                  <textarea
                    name="store_address"
                    placeholder="Enter your address"
                    rows={3}
                    className={`input-field ${errors.store_address ? "input-error" : ""}`}
                    value={formData?.store_address || ""}
                    onBlur={(e) => getLatLngFromAddress(e.target.value)}
                    onChange={handleChange}
                  />
                  {errors.store_address && (
                    <span className="text-danger">{errors.store_address}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="store_landmark" className="input-label">
                    {t("landmark")}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="store_landmark"
                    placeholder="Enter your landmark"
                    className={`input-field ${errors.store_landmark ? "input-error" : ""}`}
                    value={formData?.store_landmark || ""}
                    onChange={handleChange}
                  />
                  {errors.store_landmark && (
                    <span className="text-danger">{errors.store_landmark}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="store_pincode" className="input-label">
                    {t("pincode")}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData?.store_pincode || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                      setFormData((prev) => ({
                        ...prev,
                        store_pincode: value,
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        store_pincode: validateField("store_pincode", value, {
                          ...formData,
                          store_pincode: value,
                        }),
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className={`input-field ${errors.store_city_id ? "input-error" : ""}`}
                  />
                  {errors.store_pincode && (
                    <span className="text-danger">{errors.store_pincode}</span>
                  )}
                </div>
                {/* Country & State */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="store_country_id" className="input-label">
                    {t("country")}
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={countryList}
                    value={
                      countryList.find(
                        (opt) => opt.value === formData.store_country_id,
                      ) || null
                    }
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        store_country_id: selected?.value ?? "",
                        store_state_id: "",
                        store_city_id: "",
                      }));
                      setStateList([]);
                      setCityList([]);
                    }}
                    styles={customStyles}
                    placeholder="Select country"
                    isClearable={true}
                    isSearchable={true}
                  />
                  {errors.store_country_id && (
                    <span className="text-danger">
                      {errors.store_country_id}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="input-label">
                    {t("state")}
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={stateList}
                    value={
                      stateList.find(
                        (opt) => opt.value === formData.store_state_id,
                      ) || null
                    }
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        store_state_id: selected?.value ?? "",
                        store_city_id: "",
                      }));
                      setCityList([]);
                    }}
                    styles={customStyles}
                    placeholder="Select state"
                    isClearable={true}
                    isSearchable={true}
                  />
                  {errors.store_state_id && (
                    <span className="text-danger">{errors.store_state_id}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="store_city_id" className="input-label">
                    {t("city")}
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={cityList}
                    value={
                      cityList.find(
                        (opt) => opt.value === formData.store_city_id,
                      ) || null
                    }
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        store_city_id: selected?.value ?? "",
                      }))
                    }
                    styles={customStyles}
                    placeholder="Select city"
                    isClearable={true}
                    isSearchable={true}
                  />
                  {errors.store_city_id && (
                    <span className="text-danger">{errors.store_city_id}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="store_business_type" className="input-label">
                    {t("businessType")}
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={businessOptions}
                    value={selectedOption}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        store_business_type: selected?.value || "",
                      }))
                    }
                    styles={customStyles}
                    placeholder="Select business type"
                  />
                  {errors.store_business_type && (
                    <span className="text-danger">
                      {errors.store_business_type}
                    </span>
                  )}
                </div>
              </div>
              <div className="h-[300px] rounded-[24px] overflow-hidden border-2 border-dashed border-slate-200">
                <div ref={mapRef} className="w-full h-full" />
              </div>
            </div>

            <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-4">
              {/* <button
                type="button"
                className="btn-filter-close"
                onClick={() => navigate("/store")}
              >
                {t("close")}
              </button> */}
              <button
                type="button"
                className="btn-submit-orange flex items-center gap-2 mb-1"
                onClick={handleSubmit}
                disabled={btnLoading}
              >
                {btnLoading && (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {btnLoading
                  ? t("submitting")
                  : formData.uuid
                    ? t("update")
                    : t("submit")}
              </button>
            </div>
          </section>
        </div>
      </div>
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#161e2d] rounded-3xl p-6 w-[90%] max-w-sm shadow-2xl animate-fade-in">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
              Confirm Logout?
            </h2>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              You will be logged out of your account and need to login again.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 transition"
              >
                Cancel
              </button>

              <button
                onClick={(e) => {
                  setShowLogoutModal(false);
                  onLogout(e);
                }}
                className="px-4 py-2 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StoreFrontAdd;
